import { TICK_DT } from '../core/constants.ts';
import { dist } from '../core/math.ts';
import type { Vec3 } from '../core/math.ts';
import { sanitiseInput } from '../core/inputGuard.ts';
import type { PlayerState } from '../core/player.ts';
import { predictTick, type PlayerInput, type SimState, type WorldQuery } from '../core/sim.ts';

/**
 * Running your own mage before the server has agreed to it.
 *
 * Without this a player presses W and waits for a round trip before anything
 * moves. On a local network that is invisible; across an ocean it is walking
 * through treacle, and no amount of interpolation hides it — interpolation
 * smooths *other people*, and the one thing it cannot smooth is the gap between
 * a key and your own feet.
 *
 * So the client simulates the one mage it controls, immediately, and keeps every
 * input it has sent. When the room answers, it says which input it had got up to;
 * everything after that is replayed on top of the position the room gave. If the
 * two agree — which they do almost always, because both ran the same code over
 * the same inputs — nothing visible happens at all.
 *
 * This is what every fast shooter does and has done since Quake. The parts that
 * matter are that both ends run *the same movement code* (they do: `movePlayer`
 * in core), that the client keeps its inputs until they are acknowledged, and
 * that a correction is eased in rather than snapped — because a snap is a
 * teleport, and a teleport is far more noticeable than the error it fixes.
 */

/**
 * How far wrong the prediction may be before it is worth correcting visibly.
 *
 * Under this the error is simply carried: floating-point drift between two
 * machines running the same arithmetic is real and permanent, and chasing it
 * produces a mage that jitters in place while being, in every way that matters,
 * in the right position.
 */
const FORGIVE = 0.03;

/**
 * Past this, the prediction was not wrong so much as talking about a different
 * match — a teleport, a resurrection, a shove nobody predicted. Snapped, because
 * easing a mage twenty metres looks like a bug and arriving does not.
 */
const SNAP = 4;

/** How quickly a small correction is eased away, as a fraction per second. */
const EASE_PER_SECOND = 8;

/** How many unacknowledged inputs to keep. Two seconds of them at sixty hertz. */
const KEEP = 128;

interface Sent {
  seq: number;
  input: PlayerInput;
  dt: number;
}

export class Prediction {
  /** Position before the most recent predicted tick. */
  private from: Vec3 | null = null;

  /** The next sequence number to hand out. */
  private seq = 0;
  private readonly pending: Sent[] = [];

  /**
   * Where this client believes it is, kept apart from the snapshot.
   *
   * A separate object rather than an edit to the received state, because the
   * received state is replaced wholesale every time one arrives — writing a
   * predicted position into it would be overwritten a frame later, which is the
   * bug that makes people conclude prediction "does not work".
   */
  private mage: PlayerState | null = null;

  /**
   * What is left of a correction, worked off over the following frames.
   *
   * Held as an offset rather than applied to the position, so the simulation
   * continues from where the *server* said and only the drawing lags behind.
   * Correcting the simulation instead would mean predicting from a position the
   * server has already disagreed with.
   */
  private error: Vec3 = { x: 0, y: 0, z: 0 };

  /** The last input the room said it had processed. */
  private acked = -1;

  /** Stamps an input, remembers it, and hands it on to be sent. */
  /**
   * Drops the unacknowledged queue and the predicted mage.
   *
   * Called when the round is not running — a pre-round freeze, an intermission —
   * because there is nothing to predict when the server is holding everybody
   * still, and inputs queued against a frozen simulation will never be
   * acknowledged in a form worth replaying. The next live frame starts again
   * from where the server actually has us.
   */
  forget(): void {
    this.pending.length = 0;
    this.mage = null;
    this.from = null;
  }

  record(input: PlayerInput, dt: number): PlayerInput & { seq: number } {
    const stamped = { ...input, seq: this.seq };
    this.pending.push({ seq: this.seq, input: { ...input }, dt });
    if (this.pending.length > KEEP) this.pending.shift();
    this.seq += 1;
    return stamped;
  }

  /**
   * Runs the local mage forward on this frame's input.
   *
   * The same `movePlayer` the room runs, against the same level, over the same
   * input — which is the whole reason a prediction is usually right. Anything
   * that made the two differ, however small, would show up as a constant stream
   * of corrections.
   */
  step(input: PlayerInput, world: WorldQuery, dt: number): void {
    if (!this.mage) return;
    this.from = { ...this.mage.pos };
    predictTick(this.mage, sanitiseInput(input), world, dt);
    this.decay(dt);
  }

  /**
   * Takes what the room said and replays everything it had not yet seen.
   *
   * `ack` is the last input the room processed. Its position is therefore the
   * truth as of that input and nothing later, so every input after it is run
   * again on top — arriving at where this client should be *now*, given what the
   * room knows and what has happened since.
   */
  reconcile(
    authoritative: PlayerState, ack: number, world: WorldQuery,
  ): void {
    if (!this.mage) {
      this.mage = clonePlayer(authoritative);
      this.acked = ack;
      return;
    }

    const believed = { ...this.mage.pos };

    // Everything about the mage comes from the room — health, power, effects,
    // level. Only the *position* is this client's business to predict.
    const merged = clonePlayer(authoritative);
    merged.pos = { ...authoritative.pos };
    this.mage = merged;

    this.acked = ack;
    while (this.pending.length && this.pending[0]!.seq <= ack) this.pending.shift();

    this.from = { ...this.mage.pos };
    for (const sent of this.pending) {
      this.from = { ...this.mage.pos };
      predictTick(this.mage, sanitiseInput(sent.input), world, sent.dt);
    }

    /*
     * How wrong were we, and does anybody need to see it?
     *
     * Almost always: not very, and no. The offset is what the drawing carries
     * while the simulation gets on with the corrected position — so a small
     * disagreement is worked off over a few frames and never appears as a jump.
     */
    const off = dist(believed, this.mage.pos);
    if (off > SNAP) {
      this.error = { x: 0, y: 0, z: 0 };
    } else if (off > FORGIVE) {
      this.error = {
        x: believed.x - this.mage.pos.x,
        y: believed.y - this.mage.pos.y,
        z: believed.z - this.mage.pos.z,
      };
    }
  }

  /** Eases a correction away rather than letting it sit. */
  private decay(dt: number): void {
    const keep = Math.max(0, 1 - EASE_PER_SECOND * dt);
    this.error = {
      x: this.error.x * keep,
      y: this.error.y * keep,
      z: this.error.z * keep,
    };
    if (Math.hypot(this.error.x, this.error.y, this.error.z) < 0.002) {
      this.error = { x: 0, y: 0, z: 0 };
    }
  }

  /** Where the local mage should be drawn: predicted, less what is being eased off. */
  get drawn(): PlayerState | null {
    if (!this.mage) return null;
    const from = this.from ?? this.mage.pos;
    return {
      ...this.mage,
      pos: {
        x: this.mage.pos.x + this.error.x,
        y: this.mage.pos.y + this.error.y,
        z: this.mage.pos.z + this.error.z,
      },
      /*
       * Where the latest predicted tick began, so the camera can be drawn part
       * way through it (`RoomTransport.localAlpha`) rather than stepping sixty
       * times a second at three hundred frames. Without this the predicted mage
       * carried the *server's* previous position, a snapshot old.
       */
      prevPos: { x: from.x + this.error.x, y: from.y + this.error.y, z: from.z + this.error.z },
    };
  }

  /** How many inputs are in flight. Large and growing means the room is not answering. */
  get inFlight(): number {
    return this.pending.length;
  }

  get lastAcked(): number {
    return this.acked;
  }

  /**
   * Puts the predicted mage into a received world.
   *
   * The renderer draws whatever `getState` returns, so the predicted position
   * has to be *in* that state rather than beside it — a mage in two places is a
   * mage the nameplates, the sounds and the camera all disagree about.
   */
  applyTo(state: SimState, id: string): SimState {
    const drawn = this.drawn;
    if (!drawn) return state;

    const players = state.players.map((p) => (p.id === id ? drawn : p));
    return { ...state, players };
  }
}

/**
 * A copy deep enough that predicting cannot corrupt what the room sent.
 *
 * Shallow would share `pos`, `vel` and `effects` with the snapshot, and
 * `movePlayer` writes to all three — so a single frame of prediction would edit
 * the authoritative state it was supposed to be measured against.
 */
function clonePlayer(p: PlayerState): PlayerState {
  return {
    ...p,
    pos: { ...p.pos },
    vel: { ...p.vel },
    effects: p.effects.map((e: PlayerState['effects'][number]) => ({ ...e })),
  };
}

/** The tick the client assumes when it has not been told otherwise. */
export const PREDICT_DT = TICK_DT;
